import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useCallHistory, useDeal } from '../../api/queries/dealDetail'
import { userFacingMessage } from '../../api/errors'
import { Button } from '../../ui/Button'
import { ErrorState, Skeleton } from '../../ui/Feedback'
import { RISK_LABEL } from '../pipeline/labels'
import { formatDate } from '../../lib/format'
import { CallHistory } from './CallHistory'
import { DealActions } from './DealActions'
import { DecisionButtons } from './DecisionButtons'
import { LeadDetails } from './LeadDetails'
import { NotesEditor } from './NotesEditor'
import { OutcomeBar } from './OutcomeBar'
import { Prose } from './Prose'
import { SignalEvidence } from './SignalEvidence'
import { Verdict } from './Verdict'
import { WorkflowContext } from './WorkflowContext'
import { UploadDialog } from '../upload/UploadDialog'
import styles from './DealPanel.module.css'

/**
 * The deal panel: the evidence behind one verdict.
 *
 * A route, not a modal flag, so it is linkable, survives a refresh and gets its
 * own loading and error handling (docs/16 section 3.2). Read-only in this
 * increment — decisions, notes and participant editing come next.
 */
export function DealPanel() {
  const { dealId = '' } = useParams()
  const navigate = useNavigate()
  const deal = useDeal(dealId)
  const calls = useCallHistory(dealId)
  const [addingCall, setAddingCall] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const restoreTo = useRef<Element | null>(null)

  function close() {
    navigate('/', { replace: false })
  }

  // Focus management: move focus in on open, restore it on close, and trap Tab
  // inside while open. The old app showed overlays by adding a class, which left
  // keyboard users tabbing through the board behind the panel.
  useEffect(() => {
    restoreTo.current = document.activeElement
    closeRef.current?.focus()
    const previous = restoreTo.current
    return () => {
      if (previous instanceof HTMLElement) previous.focus()
    }
  }, [])

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        close()
        return
      }
      if (event.key !== 'Tab' || !panelRef.current) return
      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])',
      )
      if (focusable.length === 0) return
      const first = focusable[0]!
      const last = focusable[focusable.length - 1]!
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  })

  return (
    <>
      <div className={styles.overlay} onClick={close} aria-hidden="true" />
      <div
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-label={deal.data ? `${deal.data.companyName} deal details` : 'Deal details'}
        ref={panelRef}
      >
        <header className={styles.head}>
          <div>
            <h2 className={styles.name}>{deal.data?.companyName ?? 'Loading…'}</h2>
            <p className={styles.sub}>
              {[deal.data?.contactTitle || 'Deal', deal.data?.country].filter(Boolean).join(' · ')}
              {deal.data?.latestCallDate ? ` · Last call: ${formatDate(deal.data.latestCallDate)}` : ''}
            </p>
          </div>
          <Button ref={closeRef} onClick={close} aria-label="Close deal details">
            Close
          </Button>
        </header>

        {deal.data ? (
          <div className={styles.decisions}>
            {/* Labelled because it is the USER's decision, not DealPulse's. Sitting
                unlabelled and in colour above everything else, it read as the
                system's verdict — Mike misread his own panel this way, which is
                as good a test as any that nobody else would get it right. */}
            <p className={styles.decisionsLabel}>Your decision</p>
            <DecisionButtons
              dealId={dealId}
              current={deal.data.lastDecision}
              variant="panel"
              promptWhenUndecided
            />
          </div>
        ) : null}

        <div className={styles.body}>
          {deal.isPending ? (
            <div className={styles.loading}>
              <Skeleton height={20} width="60%" />
              <Skeleton height={80} />
              <Skeleton height={120} />
            </div>
          ) : deal.isError ? (
            <ErrorState
              title="Could not load this deal"
              body={userFacingMessage(deal.error)}
              action={<Button onClick={() => void deal.refetch()}>Try again</Button>}
            />
          ) : (
            <>
              <LeadDetails deal={deal.data} />

              <OutcomeBar deal={deal.data} />

              {/* The reasoning flow, in the order a person actually reasons:
                  what the calls established, what stands in the way, what
                  follows from that, and where the user disagreed. Position in
                  the pipeline comes last — it is context, not an argument. */}
              <SignalEvidence
                confirmed={deal.data.confirmedQualification}
                missing={deal.data.missingQualification}
              />

              {deal.data.activeRisks.length > 0 || deal.data.blockingFacts.length > 0 ? (
                <section>
                  <h3 className={styles.section}>Risks and blockers</h3>
                  <ul className={styles.risks}>
                    {deal.data.activeRisks.map((key) => (
                      <li key={key}>{RISK_LABEL[key]}</li>
                    ))}
                  </ul>
                  {deal.data.blockingFacts.map((fact, index) => (
                    <div key={index} className={styles.blocker}>
                      <p className={styles.blockerFact}>{fact.fact}</p>
                      {fact.quote ? <blockquote className={styles.citation}>“{fact.quote}”</blockquote> : null}
                      {fact.changeEvent ? <p className={styles.meta}>Waiting for: {fact.changeEvent}</p> : null}
                    </div>
                  ))}
                </section>
              ) : null}

              <Verdict
                recommendation={deal.data.recommendation}
                reason={deal.data.recommendationReason}
                citation={deal.data.recommendationCitation}
                mandatoryReview={deal.data.mandatoryReview}
                missingEvidence={deal.data.missingEvidence}
                decision={deal.data.lastDecision}
              />

              <WorkflowContext stage={deal.data.stage} priority={deal.data.priority} />

              {/* The narrative sits below the reasoning, not above it. It is the
                  longest thing on the panel and reading it is optional once the
                  verdict is understood; with it first, the evidence began about
                  twenty lines down and "what is still missing" was something you
                  had to go looking for. */}
              {deal.data.businessContext ? (
                <section>
                  <h3 className={styles.section}>Business context</h3>
                  <Prose text={deal.data.businessContext} />
                </section>
              ) : null}

              <section>
                <h3 className={styles.section}>Deal summary</h3>
                {deal.data.dealSummary ? (
                  <Prose text={deal.data.dealSummary} />
                ) : (
                  <p className={styles.absent}>No summary yet. One appears once a call has been analysed.</p>
                )}
              </section>

              <NotesEditor dealId={dealId} notes={deal.data.userNotes} />

              <div className={styles.panelActions}>
                {/* Filled, not outlined: this is the one thing the panel exists to
                    lead to. Everything else on the deal reports what happened;
                    this is what makes something happen next. */}
                <Button variant="primary" onClick={() => setAddingCall(true)}>
                  Add a call transcript
                </Button>
              </div>

              <CallHistory query={calls} />

              <DealActions deal={deal.data} onDeleted={close} />
            </>
          )}
        </div>
      </div>
      {addingCall ? <UploadDialog dealId={dealId} onClose={() => setAddingCall(false)} /> : null}
    </>
  )
}
